import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  collection, getDocs, addDoc, deleteDoc, doc, updateDoc,
  query, orderBy, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import useAdminAuth from '../../../hooks/useAdminAuth';
import {
  HiPlus, HiOutlineTrash, HiOutlineArrowUpTray,
  HiOutlineDocument, HiOutlineFilm, HiOutlinePhoto,
  HiOutlinePencilSquare, HiOutlineArrowDownTray,
  HiOutlineTag,
} from 'react-icons/hi2';
import { IoSearchOutline, IoCloseCircle } from 'react-icons/io5';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { friendlyTimestamp } from '../../../utils/dateFormat';

const getFileType = (fileName) => {
  if (!fileName) return 'file';
  const ext = fileName.split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv'].includes(ext)) return 'video';
  return 'file';
};

const TYPE_CONFIG = {
  pdf: { label: 'PDF', icon: HiOutlineDocument, color: '#EF4444', bg: '#FEF2F2' },
  doc: { label: 'DOC', icon: HiOutlineDocument, color: '#2563EB', bg: '#EFF6FF' },
  excel: { label: 'EXCEL', icon: HiOutlineDocument, color: '#16A34A', bg: '#F0FDF4' },
  ppt: { label: 'PPT', icon: HiOutlineDocument, color: '#EA580C', bg: '#FFF7ED' },
  image: { label: '이미지', icon: HiOutlinePhoto, color: '#22C55E', bg: '#F0FDF4' },
  video: { label: '동영상', icon: HiOutlineFilm, color: '#A855F7', bg: '#FAF5FF' },
  file: { label: '파일', icon: HiOutlineDocument, color: '#4A7CFE', bg: '#EEF2FF' },
};

const CATEGORY_OPTIONS = ['수업자료', '교재', '시험지', '참고영상', '안내문', '기타'];

const AdminMaterialsPage = () => {
  const { canEdit, canDelete, admin } = useAdminAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  // 업로드 폼
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVideoUrl, setUploadVideoUrl] = useState('');
  const [uploadType, setUploadType] = useState('file');
  const [uploading, setUploading] = useState(false);

  // 필터
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // 수정 모달
  const [editMat, setEditMat] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const matSnap = await getDocs(query(collection(db, 'materials'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] }));
    setMaterials(matSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // 업로드
  const handleUpload = async () => {
    if (!uploadTitle) return;
    setUploading(true);

    let fileUrl = '';
    let storagePath = '';
    let fileName = '';
    let fileType = 'file';

    if (uploadType === 'file' && uploadFile) {
      const storageRef = ref(storage, `materials/${Date.now()}_${uploadFile.name}`);
      await uploadBytes(storageRef, uploadFile);
      fileUrl = await getDownloadURL(storageRef);
      storagePath = storageRef.fullPath;
      fileName = uploadFile.name;
      fileType = getFileType(uploadFile.name);
    } else if (uploadType === 'video_url' && uploadVideoUrl) {
      fileUrl = uploadVideoUrl;
      fileType = 'video';
    } else {
      setUploading(false);
      return;
    }

    const tags = uploadTags.split(',').map((t) => t.trim()).filter(Boolean);

    const data = {
      title: uploadTitle,
      description: uploadDesc,
      category: uploadCategory || '기타',
      tags,
      fileUrl,
      storagePath,
      fileName,
      fileType,
      createdBy: admin?.name || '관리자',
      createdByUid: admin?.uid || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'materials'), data);
    setMaterials([{ id: docRef.id, ...data }, ...materials]);

    setUploadTitle(''); setUploadDesc(''); setUploadCategory('');
    setUploadTags(''); setUploadFile(null); setUploadVideoUrl('');
    setUploadType('file'); setShowUpload(false); setUploading(false);
  };

  // 삭제
  const handleDelete = async (mat) => {
    if (!window.confirm(`'${mat.title}' 자료를 삭제하시겠습니까?`)) return;
    if (mat.storagePath) {
      try { await deleteObject(ref(storage, mat.storagePath)); } catch {}
    }
    await deleteDoc(doc(db, 'materials', mat.id));
    setMaterials(materials.filter((m) => m.id !== mat.id));
  };

  // 수정
  const startEdit = (mat) => {
    setEditMat(mat);
    setEditTitle(mat.title);
    setEditDesc(mat.description || '');
    setEditCategory(mat.category || '');
    setEditTags(mat.tags?.join(', ') || '');
    setEditFile(null);
  };
  const saveEdit = async () => {
    if (!editMat) return;
    setEditSaving(true);
    const updates = {
      title: editTitle,
      description: editDesc,
      category: editCategory || '기타',
      tags: editTags.split(',').map((t) => t.trim()).filter(Boolean),
      updatedAt: Timestamp.now(),
    };
    // 파일 교체
    if (editFile) {
      // 기존 파일 삭제
      if (editMat.storagePath) {
        try { await deleteObject(ref(storage, editMat.storagePath)); } catch {}
      }
      const storageRef = ref(storage, `materials/${Date.now()}_${editFile.name}`);
      await uploadBytes(storageRef, editFile);
      updates.fileUrl = await getDownloadURL(storageRef);
      updates.storagePath = storageRef.fullPath;
      updates.fileName = editFile.name;
      updates.fileType = getFileType(editFile.name);
    }
    await updateDoc(doc(db, 'materials', editMat.id), updates);
    setMaterials(materials.map((m) => m.id === editMat.id ? { ...m, ...updates } : m));
    setEditMat(null);
    setEditSaving(false);
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return friendlyTimestamp(ts);
  };

  const KNOWN_TYPES = ['pdf', 'doc', 'excel', 'ppt', 'image', 'video'];

  // 필터링
  const filtered = materials.filter((m) => {
    const ft = (m.fileType || '').toLowerCase();
    if (filterType === 'file' && KNOWN_TYPES.includes(ft)) return false;
    else if (filterType !== 'all' && filterType !== 'file' && ft !== filterType) return false;
    if (filterCategory !== 'all' && m.category !== filterCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = m.title?.toLowerCase().includes(q)
        || m.description?.toLowerCase().includes(q)
        || m.tags?.some((t) => t.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });
  const typeCounts = {
    all: materials.length,
    pdf: materials.filter((m) => (m.fileType || '').toLowerCase() === 'pdf').length,
    doc: materials.filter((m) => (m.fileType || '').toLowerCase() === 'doc').length,
    excel: materials.filter((m) => (m.fileType || '').toLowerCase() === 'excel').length,
    ppt: materials.filter((m) => (m.fileType || '').toLowerCase() === 'ppt').length,
    image: materials.filter((m) => (m.fileType || '').toLowerCase() === 'image').length,
    video: materials.filter((m) => (m.fileType || '').toLowerCase() === 'video').length,
    file: materials.filter((m) => !KNOWN_TYPES.includes((m.fileType || '').toLowerCase())).length,
  };

  // 사용 중인 카테고리 목록
  const usedCategories = [...new Set(materials.map((m) => m.category).filter(Boolean))];

  if (loading) return <Container><LoadingSpinner /></Container>;

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>자료 관리</PageTitle>
          <PageDesc>수업에 사용할 템플릿 자료를 등록하고 관리합니다 · 전체 {materials.length}개</PageDesc>
        </div>
        {canEdit && (
          <AddButton onClick={() => setShowUpload(!showUpload)}>
            <HiPlus size={16} /> 자료 추가
          </AddButton>
        )}
      </PageHeader>

      {/* 업로드 모달 */}
      {showUpload && (
        <Overlay onClick={() => setShowUpload(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>새 템플릿 자료 추가</ModalTitle>
              <CloseBtn onClick={() => setShowUpload(false)}><IoCloseCircle size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <UploadGrid>
                <UploadField>
                  <Label>제목 *</Label>
                  <Input placeholder="" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
                </UploadField>
                <UploadField>
                  <Label>카테고리</Label>
                  <Select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                    <option value="">카테고리 선택</option>
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </UploadField>
                <UploadField $full>
                  <Label>설명</Label>
                  <Input placeholder="" value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} />
                </UploadField>
                <UploadField $full>
                  <Label>태그 (쉼표로 구분)</Label>
                  <Input placeholder="" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} />
                </UploadField>
                <UploadField $full>
                  <Label>자료 유형</Label>
                  <TypeToggle>
                    <TypeToggleBtn $active={uploadType === 'file'} onClick={() => setUploadType('file')}>
                      파일 업로드
                    </TypeToggleBtn>
                    <TypeToggleBtn $active={uploadType === 'video_url'} onClick={() => setUploadType('video_url')}>
                       동영상 URL
                    </TypeToggleBtn>
                  </TypeToggle>
                </UploadField>
                <UploadField $full>
                  {uploadType === 'file' ? (
                    <FileLabel>
                      <HiddenFileInput type="file" onChange={(e) => setUploadFile(e.target.files[0])} />
                      <FileButton type="button">
                        <HiOutlineArrowUpTray size={16} />
                        {uploadFile ? uploadFile.name : '파일을 선택하세요'}
                      </FileButton>
                    </FileLabel>
                  ) : (
                    <Input placeholder="" value={uploadVideoUrl} onChange={(e) => setUploadVideoUrl(e.target.value)} />
                  )}
                </UploadField>
              </UploadGrid>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setShowUpload(false)}>취소</CancelBtn>
              <PrimaryBtn onClick={handleUpload} disabled={uploading || !uploadTitle || (uploadType === 'file' ? !uploadFile : !uploadVideoUrl)}>
                {uploading ? '업로드 중...' : '추가'}
              </PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {/* 필터 바 */}
      <FilterBar>
        <FilterLeft>
          <TypeFilterGroup>
            {[
              { key: 'all', label: '전체' },
              { key: 'pdf', label: 'PDF' },
              { key: 'doc', label: 'DOC' },
              { key: 'excel', label: 'EXCEL' },
              { key: 'ppt', label: 'PPT' },
              { key: 'image', label: '이미지' },
              { key: 'video', label: '동영상' },
              { key: 'file', label: '기타' },
            ].map((f) => (
              <TypeFilterBtn key={f.key} $active={filterType === f.key} onClick={() => setFilterType(f.key)}>
                {f.label} <FilterCount>{typeCounts[f.key]}</FilterCount>
              </TypeFilterBtn>
            ))}
          </TypeFilterGroup>
        </FilterLeft>
        <FilterRight>
          {usedCategories.length > 0 && (
            <CategoryFilter value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">모든 카테고리</option>
              {usedCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </CategoryFilter>
          )}
          <SearchBox>
            <IoSearchOutline size={16} color="#9CA3AF" />
            <SearchInput placeholder="제목, 설명, 태그 검색..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            {searchQuery && <ClearBtn onClick={() => setSearchQuery('')}><IoCloseCircle size={16} /></ClearBtn>}
          </SearchBox>
        </FilterRight>
      </FilterBar>

      {/* 카드 그리드 */}
      {filtered.length === 0 ? (
        <EmptyState>
          <EmptyText>{searchQuery || filterType !== 'all' || filterCategory !== 'all' ? '검색 결과가 없습니다' : '등록된 자료가 없습니다'}</EmptyText>
          {canEdit && !searchQuery && <EmptySub>자료 추가 버튼을 눌러 템플릿 자료를 등록하세요</EmptySub>}
        </EmptyState>
      ) : (
        <CardGrid>
          {filtered.map((mat) => {
            const typeConf = TYPE_CONFIG[(mat.fileType || '').toLowerCase()] || TYPE_CONFIG.file;
            const TypeIcon = typeConf.icon;
            return (
              <MaterialCard key={mat.id}>
                <CardTopRow>
                  <CardTopLeft>
                    <TypeBadge $color={typeConf.color} $bg={typeConf.bg}>
                      <TypeIcon size={14} />
                      {typeConf.label}
                    </TypeBadge>
                  </CardTopLeft>
                  <CardActions>
                    {canEdit && (
                      <ActionBtn onClick={() => startEdit(mat)}><HiOutlinePencilSquare size={15} /></ActionBtn>
                    )}
                    {canDelete && (
                      <ActionBtn $danger onClick={() => handleDelete(mat)}><HiOutlineTrash size={15} /></ActionBtn>
                    )}
                    {mat.fileUrl && (
                      <ActionBtn as="a" href={mat.fileUrl} target="_blank" rel="noopener noreferrer">
                        <HiOutlineArrowDownTray size={15} />
                      </ActionBtn>
                    )}
                  </CardActions>
                </CardTopRow>

                <CardBody>
                  <CardTitle>{mat.title}</CardTitle>
                  <TagRow>
                    {mat.category && <CategoryBadge>{mat.category}</CategoryBadge>}
                    {mat.tags?.map((t, i) => <TagChip key={i}><HiOutlineTag size={10} />{t}</TagChip>)}
                  </TagRow>
                  {mat.description && <CardDesc>{mat.description}</CardDesc>}
                  <MetaLine>
                    <MetaText>생성 {formatDate(mat.createdAt)}</MetaText>
                    <MetaDot />
                    <MetaText>수정 {formatDate(mat.updatedAt)}</MetaText>
                    {mat.fileName && <><MetaDot /><MetaText>{mat.fileName}</MetaText></>}
                  </MetaLine>
                </CardBody>

              </MaterialCard>
            );
          })}
        </CardGrid>
      )}
      {/* 수정 모달 */}
      {editMat && (
        <Overlay onClick={() => setEditMat(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>자료 수정</ModalTitle>
              <CloseBtn onClick={() => setEditMat(null)}><IoCloseCircle size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <UploadGrid>
                <UploadField>
                  <Label>제목 *</Label>
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </UploadField>
                <UploadField>
                  <Label>카테고리</Label>
                  <Select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                    <option value="">카테고리 선택</option>
                    {[...new Set([...CATEGORY_OPTIONS, ...(editCategory && !CATEGORY_OPTIONS.includes(editCategory) ? [editCategory] : [])])].map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </UploadField>
                <UploadField $full>
                  <Label>설명</Label>
                  <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                </UploadField>
                <UploadField $full>
                  <Label>태그 (쉼표로 구분)</Label>
                  <Input value={editTags} onChange={(e) => setEditTags(e.target.value)} />
                </UploadField>
                <UploadField $full>
                  <Label>파일 변경</Label>
                  <FileLabel>
                    <HiddenFileInput type="file" onChange={(e) => setEditFile(e.target.files[0])} />
                    <FileButton type="button">
                      <HiOutlineArrowUpTray size={16} />
                      {editFile ? editFile.name : (editMat.fileName || '파일을 선택하면 교체됩니다')}
                    </FileButton>
                  </FileLabel>
                </UploadField>
              </UploadGrid>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setEditMat(null)}>취소</CancelBtn>
              <PrimaryBtn onClick={saveEdit} disabled={editSaving || !editTitle}>
                {editSaving ? '저장 중...' : '저장'}
              </PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminMaterialsPage;

// ═══════════════════════════════════════
// Styled Components
// ═══════════════════════════════════════
const Container = styled.div``;
const LoadingText = styled.p`text-align:center;padding:40px;color:#9CA3AF;font-size:14px;`;

const PageHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;`;
const PageDesc = styled.p`font-size:13px;color:#9CA3AF;margin-top:2px;`;
const AddButton = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;&:hover{background:#3B6DE8;}`;

// Modal
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const Modal = styled.div`background:white;border-radius:16px;width:560px;max-width:90vw;max-height:85vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,0.15);`;
const ModalHeader = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 24px;background:#EEF2FF;border-bottom:1px solid #D9E2FF;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin:0;`;
const CloseBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#374151;}`;
const ModalBody = styled.div`padding:20px 24px;overflow-y:auto;flex:1;`;
const ModalFooter = styled.div`display:flex;gap:8px;justify-content:flex-end;padding:16px 24px;border-top:1px solid #F3F4F6;`;
const UploadGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;`;
const UploadField = styled.div`${({ $full }) => $full && 'grid-column:1/-1;'}`;
const Label = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;`;
const Input = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;box-sizing:border-box;&:focus{border-color:#4A7CFE;outline:none;}`;
const Select = styled.select`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;`;

const TypeToggle = styled.div`display:flex;gap:8px;`;
const TypeToggleBtn = styled.button`
  display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;
  border:1px solid ${({ $active }) => $active ? '#4A7CFE' : '#E5E7EB'};
  color:${({ $active }) => $active ? '#4A7CFE' : '#6B7280'};
  background:${({ $active }) => $active ? '#EEF2FF' : 'white'};
  &:hover{border-color:#4A7CFE;}
`;

const FileLabel = styled.label`display:inline-flex;cursor:pointer;width:100%;`;
const HiddenFileInput = styled.input`display:none;`;
const FileButton = styled.span`
  display:inline-flex;align-items:center;gap:8px;padding:12px 20px;
  border:2px dashed #D1D5DB;border-radius:10px;font-size:14px;font-weight:500;
  color:#6B7280;background:#FAFBFC;cursor:pointer;width:100%;box-sizing:border-box;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  &:hover{border-color:#4A7CFE;color:#4A7CFE;background:#F0F4FF;}
`;

const CancelBtn = styled.button`padding:10px 20px;border-radius:8px;border:1px solid #E5E7EB;font-size:14px;color:#374151;&:hover{background:#F9FAFB;}`;
const PrimaryBtn = styled.button`padding:10px 20px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;

// Filter
const FilterBar = styled.div`display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:16px;flex-wrap:wrap;`;
const FilterLeft = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;`;
const FilterRight = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;`;
const TypeFilterGroup = styled.div`display:flex;gap:4px;background:#F3F4F6;border-radius:8px;padding:3px;`;
const TypeFilterBtn = styled.button`
  padding:8px 16px;border-radius:8px;font-size:13px;font-weight:700;display:flex;align-items:center;gap:6px;
  color:${({ $active }) => $active ? 'white' : '#374151'};
  background:${({ $active }) => $active ? '#4A7CFE' : 'transparent'};
  box-shadow:${({ $active }) => $active ? '0 2px 4px rgba(74,124,254,0.3)' : 'none'};
  &:hover{${({ $active }) => $active ? '' : 'background:#F3F4F6;'}}
`;
const FilterCount = styled.span`font-size:11px;font-weight:700;opacity:0.85;`;
const CategoryFilter = styled.select`padding:6px 10px;border:1px solid #E5E7EB;border-radius:8px;font-size:13px;color:#374151;`;

const SearchBox = styled.div`display:flex;align-items:center;gap:6px;padding:6px 12px;border:1px solid #E5E7EB;border-radius:8px;background:white;min-width:200px;`;
const SearchInput = styled.input`flex:1;border:none;font-size:13px;&::placeholder{color:#9CA3AF;}&:focus{outline:none;}`;
const ClearBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#374151;}`;

// Empty
const EmptyState = styled.div`text-align:center;padding:60px 20px;background:white;border-radius:12px;border:1px solid #EAEDF3;`;
const EmptyText = styled.p`font-size:16px;font-weight:600;color:#6B7280;`;
const EmptySub = styled.p`font-size:13px;color:#9CA3AF;margin-top:4px;`;

// Cards
const CardGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:16px;`;
const MaterialCard = styled.div`
  background:white;border-radius:12px;border:1px solid #EAEDF3;overflow:hidden;
  display:flex;flex-direction:column;transition:box-shadow 0.15s;
  &:hover{box-shadow:0 4px 12px rgba(0,0,0,0.06);}
`;

const CardTopRow = styled.div`display:flex;justify-content:space-between;align-items:center;padding:14px 16px 0;`;
const CardTopLeft = styled.div`display:flex;align-items:center;gap:8px;min-width:0;`;
const CreatorId = styled.span`font-size:11px;color:#9CA3AF;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const TypeBadge = styled.span`
  display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:6px;
  font-size:12px;font-weight:500;color:${({ $color }) => $color};background:${({ $bg }) => $bg};
`;
const CardActions = styled.div`display:flex;gap:2px;`;
const ActionBtn = styled.button`padding:4px;border-radius:4px;color:#9CA3AF;&:hover{color:${({ $danger }) => $danger ? '#EF4444' : '#4A7CFE'};background:${({ $danger }) => $danger ? '#FEF2F2' : '#EEF2FF'};}`;

const CardBody = styled.div`padding:14px 16px;flex:1;`;
const CardTitle = styled.h3`font-size:15px;font-weight:700;color:#111827;margin:0 0 4px;`;
const CardDesc = styled.p`font-size:13px;color:#6B7280;margin:0 0 8px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;`;

const TagRow = styled.div`display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px;`;
const CategoryBadge = styled.span`padding:2px 8px;border-radius:4px;font-size:11px;font-weight:400;color:#F59E0B;background:#FFFBEB;`;
const TagChip = styled.span`display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:400;color:#6B7280;background:#F3F4F6;`;

const MetaLine = styled.div`display:flex;align-items:center;gap:6px;flex-wrap:wrap;`;
const MetaText = styled.span`font-size:11px;color:#9CA3AF;`;
const MetaDot = styled.span`width:3px;height:3px;border-radius:50%;background:#D1D5DB;flex-shrink:0;`;

